/**
 * ground_shading — the PURE rules behind the v2 ground's lighting-side terms (surfaces agent, 2026-09-27; the
 * visual-realism additions §2.1 / §2.3 / §2.5). The shader (lib/wetness.ts `applyGroundV2`) builds the same maths in
 * TSL; these CPU oracles are what `verify:ground-rules` pins, so the constants live in ONE place.
 */
import type { GroundLayerKey } from './ground_layers.js';

/**
 * §2.1 MICRO-SHADOWING (Brinck & Maximov, "The Technical Art of Uncharted 4", 2016; Lagarde's course notes): the
 * direct light's factor for a texel of cavity AO `ao` under a light at `nDotL` (the GEOMETRY normal — the AO is baked
 * against the flat tile). A cavity is a cone whose opening (cos of its half-angle ≈ 1 − 2·ao²) the light must fall
 * inside: 1 on open ground at any sun height, a crack floor darkened under a low sun, the deepest texels even at noon.
 */
export function groundMicroShadow(nDotL: number, ao: number): number {
  return Math.min(1, Math.max(0, Math.max(0, nDotL) + 2 * ao * ao - 1));
}

/**
 * §2.2 SPECULAR OCCLUSION (Lagarde & de Rousiers, "Moving Frostbite to PBR", 2014): three r185's
 * PhysicalLightingModel.ambientOcclusion already multiplies the IBL specular by exactly this, from the material's
 * `aoNode` (the ground's `gAO`) — the harness pins that three's source still carries it.
 */
export function specularOcclusion(nDotV: number, ao: number, roughness: number): number {
  return Math.min(1, Math.max(0, Math.pow(Math.max(0, nDotV) + ao, Math.pow(2, -16 * roughness - 1)) - 1 + ao));
}

/**
 * §2.5 WETNESS BY POROSITY (Lagarde, "Water drop 3a — physically based wet surfaces", 2013; Jensen et al. 1999; see
 * `GROUND_LAYER_POROSITY`). The rain's albedo factor at wetness `wet`: porous soils darken (loam 0.75 → ×0.55 fully
 * wet — the pre-porosity global, so the dominant soil keeps its calibrated look), sand to ≈ ½ (Jensen), rock barely
 * (×0.88). The roughness factor: a FILM forms on non-porous rock at once (×0.36 → 0.1–0.2 wet), porous ground glosses
 * less until saturated (loam ×0.53 ≈ the old ×0.5). The sea's wet sand band darkens by `groundWetBand` (sand → ×0.53,
 * the coordinator's Jensen ½ of 2026-09-27; a rocky shore ≈ ×0.9 — it glosses instead).
 */
export function groundWetDarkening(porosity: number, wet: number): number { return 1 + (1 + (0.4 - 1) * porosity - 1) * wet; }
export function groundWetRoughness(porosity: number, wet: number): number { return 1 + (0.3 + 0.3 * porosity - 1) * wet; }
export function groundWetBand(porosity: number): number { return 1 + (0.53 - 1) * Math.min(1, porosity / 0.9); }

/**
 * §2.3 HEX-TILING (Mikkelsen, "Practical Real-Time Hex-Tiling", JCGT 11(3) 2022; the hextile-demo shader): the
 * dominant layer's IQ 2-fetch hid the tile grid but kept straight seams and a pattern at distance; hex-tiling fetches
 * the texture THREE times — once per vertex of the triangle-lattice cell around the pixel, each copy rotated about its
 * vertex and offset by a per-vertex random — and blends by sharpened barycentric weights × the texels' luminance
 * (the lighter texel wins: fewer ghosted double images), then a contrast gain (r 0.75). Every fetch is `textureGrad`
 * with the ROTATED gradients (mip-correct). The normal pass reuses the albedo pass's weights and turns each fetch's
 * tangent XY back by its rotation. THE ORIGIN LAW holds: the lattice is ¼ of a tile (HEX_SCALE 4) on a skewed SQUARE
 * grid (not √3-scaled), so the 1792 m period is a whole number of cells along both axes, and each vertex is hashed by
 * its id reduced into ONE period (`hexCanon`) — a rebase moves the pattern by exactly a period (nothing moves).
 */
export const HEX_SCALE = 4;
export const HEX_CONTRAST = 0.75;
export const HEX_EXP = 7;
export const HEX_LUM_FALLOFF = 0.6;
/** Rotation strength per layer: directional textures (strata bands, dune ripples, beach swash lines) do not rotate. */
export const HEX_ROT: Record<GroundLayerKey, number> = {
  loam: 1, turf: 1, litter: 1, moss: 1, rock: 1, strata: 0, gravel: 1, beach: 0.25, dune: 0, mud: 1, snow: 1, alien: 1,
};
/** The lattice: barycentric weights and the three vertex ids (skewed lattice units) of lattice point `st`. */
export function hexTriangle(stx: number, sty: number): { w: [number, number, number]; v: [[number, number], [number, number], [number, number]] } {
  const skx = stx - 0.5 * sty, sky = sty;
  const bx = Math.floor(skx), by = Math.floor(sky);
  const fx = skx - bx, fy = sky - by, tz = 1 - fx - fy;
  const s = tz < 0 ? 1 : 0, s2 = 2 * s - 1;
  return { w: [-tz * s2, s - fy * s2, s - fx * s2], v: [[bx + s, by + s], [bx + s, by + 1 - s], [bx + 1 - s, by + s]] };
}
/** A lattice vertex id reduced into one period `P` (skewed units; a world z-shift of one period moves ids by (−P/2, P)). */
export function hexCanon(vx: number, vy: number, P: number): [number, number] {
  const ky = Math.floor((vy + 0.5) / P);
  const y = vy - ky * P, x0 = vx + ky * P * 0.5;
  return [x0 - Math.floor((x0 + 0.5) / P) * P, y];
}
/** Mikkelsen's Gain3 (r = HEX_CONTRAST → k = log(1 − r)/log ½ = 2 — the shader hard-codes the square), renormalised. */
export function hexGain3(w: [number, number, number], r = HEX_CONTRAST): [number, number, number] {
  const k = Math.log(1 - r) / Math.log(0.5);
  const g = w.map((x) => (x < 0.5 ? 0.5 * (2 * x) ** k : 1 - 0.5 * (2 - 2 * x) ** k));
  const s = g[0] + g[1] + g[2];
  return [g[0] / s, g[1] / s, g[2] / s];
}

/**
 * §2.4 PARALLAX SELF-SHADOW (surfaces, 2026-09-27; Tatarchuk, "Practical Parallax Occlusion Mapping", 2006; Policarpo
 * & Oliveira's relief-mapping shadows). The parallax march finds the surface the eye sees at depth `hitD` below the
 * layer's top; a SECOND march from that point toward the key light asks whether the height field rises above the ray:
 * the ray climbs from `hitD` to the top over `steps` taps, moving `L.xz / max(L.y, 0.25)` metres sideways per metre of
 * rise, and the deepest intrusion — (ray depth − surface depth) ÷ (¼ of the layer's depth) — is the occlusion. A key
 * light at or below the horizon (y ≤ 0.05) casts none (the lamps keep their light). The result multiplies the DIRECT
 * term only, like the micro-shadow (the lighting model), never the albedo.
 *   heightAt(dx, dz) → the layer's height 0..1 at a horizontal offset (metres) from the pixel's own top-projection point
 *   hitOx/hitOz: the parallax hit's offset (metres) · hitD: its depth · depthM: the layer's parallax depth
 *   L: the world direction TO the key light (need not be normalised).
 */
export const POM_SHADOW_STEPS = 6;
export function pomSelfShadow(
  heightAt: (dx: number, dz: number) => number, hitOx: number, hitOz: number, hitD: number, depthM: number,
  L: readonly [number, number, number], steps = POM_SHADOW_STEPS,
): number {
  const len = Math.hypot(L[0], L[1], L[2]) || 1;
  const lx = L[0] / len, ly = L[1] / len, lz = L[2] / len;
  if (ly <= 0.05 || hitD <= 0) return 1;
  const sy = Math.max(ly, 0.25), sx = lx / sy, sz = lz / sy;
  let occl = 0;
  for (let k = 1; k <= steps; k++) {
    const t = k / steps;
    const rayD = hitD * (1 - t), run = hitD * t;
    const surfD = (1 - heightAt(hitOx + sx * run, hitOz + sz * run)) * depthM;
    occl = Math.max(occl, Math.min(1, Math.max(0, (rayD - surfD) / (0.25 * depthM))));
  }
  return 1 - occl;
}
